import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  IconArrowUp,
  IconArrowDown,
  IconTrash,
  IconAlertTriangle,
} from '@tabler/icons-react';
import Editor from '@monaco-editor/react';
import type {
  TaskFieldCondition,
  TaskListDefinition,
  TaskListReference,
  TaskSectionDefinition,
  TaskSectionOverride,
} from '@techgitter/contracts';
import { ConditionEditor } from '../../shared/ConditionEditor';
import type { FieldCondition } from '../../../types/ticketTypeTemplate';
import { HelpHint } from '../HelpHint';
import { EditorCard } from '../EditorCard';
import { Chip } from '../../chip';
import { FIELD_SX, PRIMARY_BTN_SX } from '../../../styles/designSystem';

const CANCEL_BTN_SX = { color: '#888', '&:hover': { color: '#555', bgcolor: 'transparent' } };

interface TaskListReferenceEditorProps {
  reference: TaskListReference;
  taskLists: TaskListDefinition[];
  onChange: (updated: TaskListReference) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export const TaskListReferenceEditor: React.FC<TaskListReferenceEditorProps> = ({
  reference,
  taskLists,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [overridesOpen, setOverridesOpen] = useState(false);

  const taskListOptions = useMemo(() => taskLists.map((tl) => tl.taskListId), [taskLists]);
  const matched = taskLists.find((tl) => tl.taskListId === reference.taskListId);
  // Only inline sections expose a sectionId that `removeSections` can
  // target. Nested TaskListReferences inside the source list don't have
  // a stable sectionId — they expand to multiple sections at resolve
  // time, each with its own ID. To remove those, drop the reference in
  // the source task list itself.
  const sectionIdOptions =
    matched?.sections.filter((s): s is TaskSectionDefinition => !('taskListId' in s)).map(
      (s) => s.sectionId,
    ) ?? [];

  const overrideCount =
    (reference.sectionOverrides?.length ?? 0) + (reference.addSections?.length ?? 0);

  const setField = <K extends keyof TaskListReference>(key: K, value: TaskListReference[K]) => {
    onChange({ ...reference, [key]: value });
  };

  const handleShowWhenChange = (next: FieldCondition | null | undefined) => {
    if (next == null) {
      const { showWhen: _omit, ...rest } = reference;
      onChange(rest as TaskListReference);
    } else {
      setField('showWhen', next as unknown as TaskFieldCondition);
    }
  };

  // sectionOverrides + addSections as a single JSON sub-mode payload.
  const advancedPayload = useMemo(
    () => ({
      sectionOverrides: reference.sectionOverrides ?? [],
      addSections: reference.addSections ?? [],
    }),
    [reference.sectionOverrides, reference.addSections],
  );
  const [advancedJson, setAdvancedJson] = useState<string>(() =>
    JSON.stringify(advancedPayload, null, 2),
  );
  const [advancedDirty, setAdvancedDirty] = useState(false);
  const [advancedError, setAdvancedError] = useState<string | null>(null);

  // Re-sync editor text when external reference data changes (e.g. catalog reload).
  React.useEffect(() => {
    if (!advancedDirty) {
      setAdvancedJson(JSON.stringify(advancedPayload, null, 2));
    }
  }, [advancedPayload, advancedDirty]);

  const handleApplyAdvanced = () => {
    let parsed: { sectionOverrides?: TaskSectionOverride[]; addSections?: TaskSectionDefinition[] };
    try {
      parsed = JSON.parse(advancedJson);
    } catch {
      setAdvancedError('Invalid JSON');
      return;
    }
    setAdvancedError(null);
    onChange({
      ...reference,
      sectionOverrides: parsed.sectionOverrides && parsed.sectionOverrides.length > 0
        ? parsed.sectionOverrides
        : undefined,
      addSections: parsed.addSections && parsed.addSections.length > 0
        ? parsed.addSections
        : undefined,
    });
    setAdvancedDirty(false);
  };

  return (
    <>
      <EditorCard
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        title={
          <>
            <Chip tone="info">Reference</Chip>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {reference.labelOverride || matched?.displayName || (
                <em style={{ color: '#aaa', fontWeight: 400 }}>(no label)</em>
              )}
            </span>
            <span className="editor-card__id">→ {reference.taskListId}</span>
            {!matched && reference.taskListId && (
              <span
                title={`Unknown task list "${reference.taskListId}"`}
                style={{ display: 'inline-flex', color: '#C07A10', flexShrink: 0 }}
              >
                <IconAlertTriangle size={15} stroke={1.5} />
              </span>
            )}
            {overrideCount > 0 && (
              <Chip tone="neutral">overrides: {overrideCount}</Chip>
            )}
          </>
        }
        actions={
          <>
            {onMoveUp && (
              <button type="button" className="editor-icon-btn" title="Move up" onClick={onMoveUp}>
                <IconArrowUp size={15} stroke={1.5} />
              </button>
            )}
            {onMoveDown && (
              <button type="button" className="editor-icon-btn" title="Move down" onClick={onMoveDown}>
                <IconArrowDown size={15} stroke={1.5} />
              </button>
            )}
            <button
              type="button"
              className="editor-icon-btn editor-icon-btn--danger"
              title="Delete reference"
              onClick={() => setDeleteConfirm(true)}
            >
              <IconTrash size={15} stroke={1.5} />
            </button>
          </>
        }
      >
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              size="small"
              freeSolo
              options={taskListOptions}
              value={reference.taskListId}
              onChange={(_, v) => setField('taskListId', v ?? '')}
              onInputChange={(_, v) => setField('taskListId', v)}
              sx={FIELD_SX}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Task List ID"
                  required
                  helperText={
                    matched
                      ? `Resolves to "${matched.displayName}" (${matched.sections.length} sections)`
                      : reference.taskListId
                        ? 'Unknown task list — will be saved as-is'
                        : 'Choose a task list to reference'
                  }
                  error={!matched && !!reference.taskListId}
                  slotProps={{ input: { ...params.InputProps, sx: { fontFamily: 'monospace' } } }}
                />
              )}
            />
          </Box>
          <HelpHint
            text={
              <Box sx={{ maxWidth: 320 }}>
                The slug of a reusable Task List from the Task Lists page.
                The reference resolves at ticket-creation time, so edits to
                the source list (new tasks, renamed sections) propagate
                automatically.
              </Box>
            }
          />
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <TextField
            size="small"
            label="Label override"
            value={reference.labelOverride ?? ''}
            onChange={(e) => setField('labelOverride', e.target.value || undefined)}
            fullWidth
            sx={FIELD_SX}
            helperText="Optional — overrides the source task list's display name"
          />
          <HelpHint
            text={
              <Box sx={{ maxWidth: 320 }}>
                Renames just this reference's heading on the ticket without
                touching the source task list. Useful when the same list is
                referenced from multiple configs that each want a different
                title. Leave blank to use the source list's own name.
              </Box>
            }
          />
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <Box sx={{ flex: 1 }}>
            <ConditionEditor
              value={reference.showWhen}
              onChange={handleShowWhenChange}
              allowCustom={false}
              label="Show When"
            />
          </Box>
          <HelpHint
            text={
              <Box sx={{ maxWidth: 320 }}>
                A condition that controls whether this section appears on a
                given ticket. Compares a ticket field value or a customer-
                context value against the value you supply. When evaluated
                false, the entire section is hidden. Leave empty to always
                show.
              </Box>
            }
          />
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              size="small"
              multiple
              freeSolo
              options={sectionIdOptions}
              value={reference.removeSections ?? []}
              onChange={(_, v) =>
                setField('removeSections', v.length > 0 ? (v as string[]) : undefined)
              }
              sx={FIELD_SX}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Remove sections"
                  helperText={
                    sectionIdOptions.length > 0
                      ? 'Choose section IDs from the referenced task list to exclude'
                      : 'Reference a known task list to pick section IDs'
                  }
                />
              )}
            />
          </Box>
          <HelpHint
            text={
              <Box sx={{ maxWidth: 320 }}>
                Section IDs from the source task list that should be
                skipped on tickets of this type. Useful when most of a list
                applies but a few sections are irrelevant — e.g. the
                "Hardware Check" section in a remote-only ticket type.
              </Box>
            }
          />
        </Stack>

        <Box>
          <Button
            size="small"
            onClick={() => setOverridesOpen((open) => !open)}
            sx={{ mb: 1, color: '#4A90D9', '&:hover': { bgcolor: '#EDF5FF' } }}
            title="Edit sectionOverrides (per-section/per-task tweaks) and addSections (extra sections layered on top of the source) as raw JSON. Use when the standard reference + remove + labelOverride aren't enough."
          >
            {overridesOpen ? 'Hide' : 'Show'} advanced overrides (JSON)
          </Button>
          <Collapse in={overridesOpen}>
            <Box sx={{ border: '0.5px solid #e2eaf3', borderRadius: '8px', overflow: 'hidden', mb: 1 }}>
              <Editor
                height="280px"
                defaultLanguage="json"
                value={advancedJson}
                onChange={(value) => {
                  setAdvancedJson(value ?? '');
                  setAdvancedDirty(true);
                }}
                options={{
                  minimap: { enabled: false },
                  fontSize: 12,
                  formatOnPaste: true,
                  scrollBeyondLastLine: false,
                }}
              />
            </Box>
            {advancedError && (
              <Typography variant="caption" color="error">
                {advancedError}
              </Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={handleApplyAdvanced}
                disabled={!advancedDirty}
                sx={PRIMARY_BTN_SX}
              >
                Apply
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setAdvancedJson(JSON.stringify(advancedPayload, null, 2));
                  setAdvancedDirty(false);
                  setAdvancedError(null);
                }}
                disabled={!advancedDirty}
                sx={CANCEL_BTN_SX}
              >
                Reset
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              sectionOverrides and addSections are edited as JSON. See the contracts for shape.
            </Typography>
          </Collapse>
        </Box>
      </EditorCard>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Reference</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this task list reference from the config?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} sx={CANCEL_BTN_SX}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteConfirm(false);
              onDelete();
            }}
            color="error"
            variant="contained"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
